import { useEffect, useMemo, useState } from "react";
import { api, formatCnpj } from "../api";
import type { Company } from "../types";

type Flash = { kind: "ok" | "error"; text: string } | null;
type Props = { companies: Company[]; canManage: boolean; reload: () => void; flash: (value: Flash) => void };

export default function FgtsAssignmentsPage({ companies, canManage, reload, flash }: Props) {
  const [selected, setSelected] = useState<number[]>([]);
  const [query, setQuery] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setSelected(companies.filter((company) => company.fgts_enabled).map((company) => company.id));
    setDirty(false);
  }, [companies]);

  const visible = useMemo(() => companies.filter((company) =>
    `${company.legal_name} ${company.trade_name} ${company.cnpj}`.toLowerCase().includes(query.toLowerCase())
  ), [companies, query]);
  const selectedSet = new Set(selected);
  function toggle(id: number) {
    setSelected(selectedSet.has(id) ? selected.filter((value) => value !== id) : [...selected, id]);
    setDirty(true);
  }
  function setVisible(checked: boolean) {
    const next = new Set(selected);
    for (const company of visible) {
      if (checked) next.add(company.id); else next.delete(company.id);
    }
    setSelected([...next]);
    setDirty(true);
  }
  async function save() {
    setSaving(true);
    try {
      await api("/api/fgts-process/companies", {
        method: "PUT", body: JSON.stringify({ company_ids: selected }),
      });
      reload();
      flash({ kind: "ok", text: "Empresas do processo FGTS Digital atualizadas." });
    } catch (error) {
      flash({ kind: "error", text: error instanceof Error ? error.message : "Falha ao salvar FGTS" });
    } finally { setSaving(false); }
  }

  return <>
    <div className="page-head"><div>
      <p className="eyebrow">APLICAÇÃO DAS REGRAS</p>
      <h1>FGTS Digital</h1>
      <p>Defina quais empresas participam da consulta atual de GFD.</p>
    </div></div>
    <div className="notice">A regra mensal é configurada em Regras de busca. A consulta atual ainda pode trazer GFD mensais e rescisórias; a separação automática será feita na ativação dos novos processos.</div>
    <section className="card assignment-card">
      <h2>Empresas participantes</h2>
      <div className="assignment-toolbar"><input className="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar empresa ou CNPJ" />
        {canManage && <div className="actions"><button className="secondary" type="button" onClick={() => setVisible(true)}>Selecionar visíveis</button><button className="secondary" type="button" onClick={() => setVisible(false)}>Remover visíveis</button></div>}</div>
      <div className="assignment-list">{visible.map((company) => <label className="assignment-row" key={company.id}>
        <input type="checkbox" checked={selectedSet.has(company.id)} disabled={!canManage} onChange={() => toggle(company.id)} />
        <span><strong>{company.trade_name || company.legal_name}</strong><small>{formatCnpj(company.cnpj)}</small></span>
        <small>{company.active ? "Ativa" : "Inativa"}</small>
      </label>)}</div>
      {!visible.length && <p className="muted">Nenhuma empresa corresponde à busca.</p>}
      {canManage && <footer className="assignment-footer"><span>{dirty ? "Alterações ainda não salvas" : "Associações salvas"}</span><button className="primary" disabled={!dirty || saving} onClick={save}>{saving ? "Salvando…" : "Salvar empresas"}</button></footer>}
    </section>
  </>;
}
